import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ordersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import useRazorpay from 'react-razorpay';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { cart, getCartTotal, clearCart } = useCart();
  const { toast } = useToast();
  const [Razorpay] = useRazorpay();
  const [processing, setProcessing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Please login to continue</h1>
          <p className="text-gray-600 mb-8">You need to be logged in to place an order</p>
          <Button size="lg" className="bg-red-600 hover:bg-red-700" onClick={login}>
            Login Now
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has purchase unlocks
    if (user.purchase_unlocks <= 0) {
      toast({
        title: "No Purchase Unlocks Available",
        description: "Please submit a review or refer friends to unlock purchases.",
        variant: "destructive"
      });
      navigate('/dashboard');
      return;
    }
    
    setProcessing(true);

    try {
      // Create Razorpay order
      const amountInPaise = Math.round(total * 100); // Convert to paise
      const orderResponse = await axios.post(`${BACKEND_URL}/api/payment/create-order`, {
        amount: amountInPaise,
        currency: 'INR',
        receipt: `order_${Date.now()}`,
        notes: {
          user_email: user?.email || formData.email,
          user_name: formData.fullName
        }
      });

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        order_id: orderResponse.data.order_id,
        name: 'Youseful.shop',
        description: 'Payment for your order',
        image: '/youseful-logo.svg',
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${BACKEND_URL}/api/payment/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            // Create order in database after successful payment
            const orderData = {
              items: cart.map(item => ({
                product_id: item.id,
                product_name: item.name,
                product_image: item.image,
                quantity: item.quantity,
                price: item.price
              })),
              subtotal,
              gst,
              total,
              shipping_address: formData,
              payment_id: response.razorpay_payment_id,
              payment_status: 'completed'
            };

            await ordersAPI.create(orderData);
            
            setProcessing(false);
            setOrderPlaced(true);
            clearCart();
            
            toast({
              title: "Payment Successful!",
              description: "Your order has been placed successfully.",
            });

            setTimeout(() => {
              navigate('/dashboard');
            }, 3000);
          } catch (error) {
            console.error('Error after payment:', error);
            toast({
              title: "Order Creation Failed",
              description: "Payment was successful but order creation failed. Please contact support.",
              variant: "destructive"
            });
          }
        },
        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#DC2626'
        }
      };

      const rzp = new Razorpay(options);
      
      rzp.on('payment.failed', function (response) {
        setProcessing(false);
        toast({
          title: "Payment Failed",
          description: response.error.description,
          variant: "destructive"
        });
      });

      rzp.open();
      setProcessing(false);
      
    } catch (error) {
      console.error('Error creating order:', error);
      setProcessing(false);
      
      if (error.response?.status === 403) {
        toast({
          title: "No Purchase Unlocks",
          description: error.response?.data?.detail || "Please submit reviews or refer friends.",
          variant: "destructive"
        });
        navigate('/dashboard');
      } else {
        toast({
          title: "Order failed",
          description: "There was an error placing your order. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <Card className="max-w-2xl mx-auto p-12 text-center">
            <CheckCircle2 className="h-24 w-24 text-green-600 mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Order Placed Successfully!</h1>
            <p className="text-gray-600 mb-8">
              Thank you for your purchase. Your order has been placed and you will receive a confirmation email shortly.
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <p className="text-sm text-gray-600 mb-2">Order Total</p>
              <p className="text-3xl font-bold text-red-600">
                ₹{Math.round(getCartTotal() * 1.18).toLocaleString()}
              </p>
            </div>
            <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const subtotal = getCartTotal();
  const gst = Math.round(subtotal * 0.18);
  const total = Math.round(subtotal * 1.18);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-8">Checkout</h1>

        {/* Unlock Warning */}
        {user.purchase_unlocks <= 1 && (
          <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-4 mb-6">
            <p className="text-orange-900 font-semibold">
              ⚠️ {user.purchase_unlocks === 1 ? 'This is your last purchase unlock!' : 'Low on purchase unlocks!'}
            </p>
            <p className="text-orange-800 text-sm mt-1">
              Submit reviews or refer friends to earn more purchase unlocks after this order.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Shipping Information */}
            <div className="md:col-span-2 space-y-6">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Shipping Information</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Payment Method</h2>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This is a mock payment for demonstration purposes. No actual charges will be made.
                  </p>
                </div>
              </Card>
            </div>

            {/* Order Summary */}
            <div>
              <Card className="p-6 sticky top-24">
                <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex gap-3">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-sm line-clamp-1">{item.name}</p>
                        <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                        <p className="text-sm font-bold">₹{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4 space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold">₹{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-semibold text-green-600">FREE</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">GST (18%)</span>
                    <span className="font-semibold">₹{gst.toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-red-600">₹{total.toLocaleString()}</span>
                  </div>
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={processing}
                >
                  {processing ? 'Processing...' : 'Place Order'}
                </Button>
              </Card>
            </div>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;
